import {
  CANDIDATE_STATUS,
  CandidateProfile
} from '@api/models/candidateApi.models';
import { useEffect, useMemo, useState } from 'react';
import { CandidateApi } from '@api/Candidate.api';
import { useExternshipPilotEnabled } from '@common/helpers/externshipHelpers/useExternshipPilotEnabled';

type FetchCandidateProfile = {
  candidateProfile?: CandidateProfile;
  profileComplete: boolean;
  loading: boolean;
  loaded: true | null;
};

/**
 * Fetch CandidateProfile from CandidateApi.getById()
 *
 * @param {number} candidateId
 * @returns `{ candidateProfile: CandidateProfile; profileComplete: boolean; loading: boolean; }`
 */
export const useFetchCandidateProfile = (
  candidateId?: number
): FetchCandidateProfile => {
  const { setIsPilotEnabled } = useExternshipPilotEnabled();

  const [candidateProfile, setCandidateProfile] = useState<CandidateProfile>();
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState<true | null>(null);

  useEffect(() => {
    if (candidateId) {
      const fetchProfile = async (): Promise<void> => {
        try {
          setLoading(true);
          const res = await CandidateApi.getById(candidateId);
          setCandidateProfile(res.data);
          setIsPilotEnabled(!!res.data.externshipPilotEnabled);
        } catch (error) {
          console.error(
            'Error for useFetchCandidateProfile -> CandidateApi.getById()',
            error
          );
        } finally {
          setLoading(false);
          setLoaded(true);
        }
      };

      fetchProfile();
    } else {
      setLoaded(true);
    }
  }, [candidateId]);

  const profileComplete = useMemo(() => {
    if (candidateProfile) {
      return candidateProfile.status === CANDIDATE_STATUS.COMPLETED;
    }

    return false;
  }, [candidateProfile]);

  return { candidateProfile, profileComplete, loading, loaded };
};
